import {
  sendShopifyAnalytics,
  getClientBrowserParameters,
  AnalyticsEventName,
  AnalyticsPageType,
  ShopifySalesChannel,
} from "@shopify/hydrogen-react";
import { safeGet } from "./api";

const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`);

const cookieDomain = () => {
  const h = window.location.hostname;
  if (/emergent|localhost|^\d+\./.test(h)) return "";
  const parts = h.split(".");
  return parts.length >= 2 ? `; domain=.${parts.slice(-2).join(".")}` : "";
};

const readCookie = (name) => document.cookie.match(new RegExp(`\\b${name}=([^;]+)`))?.[1];

const setCookie = (name, value, maxAge) => {
  if (!value) return;
  document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; SameSite=Lax; Secure${cookieDomain()}`;
};

const TOKEN_QUERY = "query ensureCookies { consentManagement { cookies(visitorConsent:{}) { trackingConsentCookie shopifyUnique shopifyVisit } } }";

// Asks Shopify (on the checkout domain) for the visitor + session tokens. Shopify also sets its own
// first-party cookies on that domain, so the checkout continues the same session as the storefront.
const fetchShopifyTokens = async (shop) => {
  const unique = readCookie("_shopify_y");
  const visit = readCookie("_shopify_s");
  const request = (credentials) => fetch(`https://${shop.checkout_domain}/api/unstable/graphql.json`, {
    method: "POST",
    credentials,
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": shop.storefront_token,
      ...(unique && visit ? { "X-Shopify-UniqueToken": unique, "X-Shopify-VisitToken": visit } : {}),
    },
    body: JSON.stringify({ query: TOKEN_QUERY }),
  });
  // Credentials are only accepted from the shop's own domains (firstmoveros.com); elsewhere fall back to tokens only.
  const res = await request("include").catch(() => request("omit"));
  const c = (await res.json())?.data?.consentManagement?.cookies;
  setCookie("_shopify_y", c?.shopifyUnique || unique || uuid(), 60 * 60 * 24 * 365);
  setCookie("_shopify_s", c?.shopifyVisit || visit || uuid(), 60 * 30);
  setCookie("_tracking_consent", c?.trackingConsentCookie, 60 * 60 * 24 * 365);
};

let shopPromise;
const getShop = () => {
  if (!shopPromise) shopPromise = safeGet("/shop", null);
  return shopPromise;
};

let sessionPromise;
export const initShopifySession = () => {
  if (!sessionPromise) {
    sessionPromise = getShop()
      .then((shop) => (shop?.checkout_domain && shop?.storefront_token ? fetchShopifyTokens(shop) : undefined))
      .catch(() => {
        setCookie("_shopify_y", readCookie("_shopify_y") || uuid(), 60 * 60 * 24 * 365);
        setCookie("_shopify_s", readCookie("_shopify_s") || uuid(), 60 * 30);
      });
  }
  return sessionPromise;
};

export const trackingTokens = async () => {
  await initShopifySession();
  return { unique: readCookie("_shopify_y"), visit: readCookie("_shopify_s") };
};

const send = async (eventName, extra = {}) => {
  try {
    const shop = await getShop();
    if (!shop?.shop_id) return;
    await initShopifySession();
    await sendShopifyAnalytics({
      eventName,
      payload: {
        ...getClientBrowserParameters(),
        hasUserConsent: true,
        analyticsAllowed: true,
        marketingAllowed: true,
        shopId: shop.shop_id,
        currency: shop.currency || "CHF",
        acceptedLanguage: "EN",
        shopifySalesChannel: ShopifySalesChannel.headless,
        ...extra,
      },
    });
  } catch {
    /* analytics must never break the page */
  }
};

const toProduct = (p, variant) => ({
  productGid: p.shopifyId,
  variantGid: variant?.id,
  name: p.name,
  variantName: variant?.title,
  brand: "First Mover OS",
  price: String(variant?.price ?? p.price),
  quantity: 1,
});

const pageTypeFor = (path) => {
  if (path === "/") return AnalyticsPageType.home;
  if (path === "/products") return AnalyticsPageType.collection;
  if (path.startsWith("/products/")) return AnalyticsPageType.product;
  return AnalyticsPageType.page;
};

export const shopifyPageView = (path) => send(AnalyticsEventName.PAGE_VIEW, { pageType: pageTypeFor(path) });

export const shopifyProductView = (p) => {
  const v = p?.variants?.[0];
  if (!p?.shopifyId) return undefined;
  return send(AnalyticsEventName.PRODUCT_VIEW, { pageType: AnalyticsPageType.product, resourceId: p.shopifyId, currency: p.currency, products: [toProduct(p, v)], totalValue: Number(v?.price ?? p.price) });
};

export const shopifyAddToCart = (p, variant, cartId) => {
  if (!p?.shopifyId) return undefined;
  return send(AnalyticsEventName.ADD_TO_CART, { cartId, currency: p.currency, products: [toProduct(p, variant)], totalValue: Number(variant?.price ?? p.price) });
};
